import {DOCUMENT, isPlatformBrowser, Location} from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {MenuItem} from 'primeng/api';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CurrentUserViewModel} from '../../core/models/current-user.view-model';
import {MobileBackCoordinatorService} from '../../core/mobile/mobile-back-coordinator.service';
import {NotificationDataProviderService} from '../../core/services/notification.data-provider.service';
import {ViewTypeEnum} from '../../core/enums/view-type.enum';
import {ActionsService} from '../footer/actions.service';
import {AppBreadcrumbService} from './app.breadcrumb.service';
import {BottomSheetComponent} from '../components/bottom-sheet/bottom-sheet.component';
import {MobileSearchOverlayService} from '../../core/mobile/mobile-search-overlay.service';
import {QueryService} from '../../core/services/query.service';

interface PointerStart {
  x: number;
  y: number;
  target: EventTarget | null;
}

type HubResourceId = 'places' | 'ranking' | 'marketplace' | 'profile' | 'garage';

interface HubResource {
  id: HubResourceId;
  icon: string;
  labelKey: string;
}

@Component({
  selector: 'app-mobile-top-navigation',
  templateUrl: './mobile-top-navigation.component.html',
  styleUrls: ['./mobile-top-navigation.component.scss'],
})
export class MobileTopNavigationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public user: CurrentUserViewModel | null = null;
  @Output() public readonly drawerRequested = new EventEmitter<void>();
  @Output() public readonly collapsedChange = new EventEmitter<boolean>();
  @ViewChild('hubBottomSheet') private hubBottomSheet?: BottomSheetComponent;
  @ViewChild('mobileSearchInput') private mobileSearchInput?: ElementRef<HTMLInputElement>;

  public readonly ViewTypeEnum = ViewTypeEnum;
  public collapsed = false;
  public hubOpen = false;
  public searchOpen = false;
  public searchQuery = '';
  public breadcrumbItems: MenuItem[] = [];
  public unreadCount = 0;
  public isDetailView = false;
  public readonly hubResources: HubResource[] = [
    {id: 'places', icon: 'pi pi-map-marker', labelKey: 'common.places'},
    {id: 'ranking', icon: 'pi pi-star', labelKey: 'common.ranking'},
    {id: 'marketplace', icon: 'pi pi-shopping-bag', labelKey: 'common.marketplace'},
    {id: 'profile', icon: 'pi pi-user', labelKey: 'common.profile'},
    {id: 'garage', icon: 'pi pi-warehouse', labelKey: 'common.garage'},
  ];

  private readonly subscriptions = new Subscription();
  private removeHubBackHandler: (() => void) | null = null;
  private removeSearchBackHandler: (() => void) | null = null;
  private removePointerDownListener: (() => void) | null = null;
  private removePointerUpListener: (() => void) | null = null;
  private removeScrollListener: (() => void) | null = null;
  private initialScrollFrameId: number | null = null;
  private initialScrollTimeoutId: number | null = null;
  private pointerStart: PointerStart | null = null;
  private notificationSourceForUserId: number | null = null;

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    public readonly breadcrumb: AppBreadcrumbService,
    public readonly actionsService: ActionsService,
    private readonly notifications: NotificationDataProviderService,
    private readonly mobileBack: MobileBackCoordinatorService,
    private readonly mobileSearchOverlay: MobileSearchOverlayService,
    private readonly queryService: QueryService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
  }

  public ngOnInit(): void {
    this.subscriptions.add(this.breadcrumb.itemsHandler.subscribe((items) => {
      this.breadcrumbItems = items || [];
      this.updateDetailState();
    }));
    this.subscriptions.add(this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.updateDetailState();
      this.closeSearch();
    }));
    this.subscriptions.add(this.notifications.unreadCount$.subscribe((count) => this.unreadCount = count));
    this.subscriptions.add(this.mobileSearchOverlay.open$.subscribe((open) => this.onSearchOverlayStateChange(open)));
    this.attachScrollListener();
    this.attachEdgeSwipe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user) {
      this.connectNotifications();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.removeHubBackHandler?.();
    this.removeSearchBackHandler?.();
    this.removePointerDownListener?.();
    this.removePointerUpListener?.();
    this.removeScrollListener?.();
    if (this.initialScrollFrameId !== null && isPlatformBrowser(this.platformId)) {
      cancelAnimationFrame(this.initialScrollFrameId);
    }
    if (this.initialScrollTimeoutId !== null && isPlatformBrowser(this.platformId)) {
      window.clearTimeout(this.initialScrollTimeoutId);
    }
  }

  public get hubTitle(): string {
    return this.breadcrumbItems
      .map((item) => item?.label)
      .filter((label) => !!label)
      .join(' · ') || 'WTG';
  }

  public get canChangeView(): boolean {
    return this.actionsService.viewChangeEnabled;
  }

  public get hubMiddleBreakHeight(): number {
    const handleHeight = 24;
    const contextHeight = this.breadcrumbItems.length ? 22 : 0;
    const listHeight = this.hubResources.length * 48 + Math.max(0, this.hubResources.length - 1) * 6;
    const spacing = contextHeight ? 10 : 0;
    const verticalPadding = 16;
    return handleHeight + contextHeight + spacing + listHeight + verticalPadding;
  }

  public get viewChangeIcon(): string {
    return this.actionsService.viewType === ViewTypeEnum.LIST ? 'pi pi-th-large' : 'pi pi-list';
  }

  public onLeadingAction(): void {
    if (this.isDetailView) {
      this.location.back();
      return;
    }
    this.drawerRequested.emit();
  }

  public openHub(): void {
    if (this.hubOpen) {
      return;
    }
    this.hubOpen = true;
    this.removeHubBackHandler = this.mobileBack.register('mobile-top-navigation-hub', () => {
      this.closeHub();
      return true;
    });
    void this.presentHub();
  }

  public closeHub(): void {
    this.hubOpen = false;
    this.removeHubBackHandler?.();
    this.removeHubBackHandler = null;
    void this.hubBottomSheet?.closeIfPresented();
  }

  public onHubDismissed(): void {
    this.closeHub();
  }

  public navigateTo(resource: HubResourceId): void {
    const currentUserSlug = this.user?.slug;
    const routes = {
      places: ['/places'],
      ranking: ['/ranking'],
      marketplace: ['/marketplace'],
      profile: currentUserSlug ? ['/', currentUserSlug, 'profile'] : ['/login'],
      garage: currentUserSlug ? ['/', currentUserSlug, 'garage'] : ['/login'],
    };
    this.closeHub();
    this.router.navigate(routes[resource]);
  }

  public isCurrentHubResource(resource: HubResourceId): boolean {
    const path = this.router.url.split('?')[0];
    if (resource === 'places') {
      return path === '/places';
    }
    if (resource === 'ranking') {
      return path === '/ranking';
    }
    if (resource === 'marketplace') {
      return path === '/marketplace' || path === '/marketplace/offers';
    }

    const userSlug = this.user?.slug;
    return !!userSlug && path.startsWith('/' + userSlug + '/' + resource);
  }

  public openNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  public openFilters(): void {
    this.breadcrumb.emitFiltersClicked();
  }

  public closeSearch(): void {
    this.mobileSearchOverlay.close();
  }

  public submitSearch(): void {
    const query = this.searchQuery.trim();
    this.closeSearch();
    this.router.navigate(['/search-results'], {queryParams: {q: query}});
  }

  public onSearchQueryChange(query: string): void {
    this.queryService.resultSubject$.next(query);
  }

  public toggleView(): void {
    const next = this.actionsService.viewType === ViewTypeEnum.LIST ? ViewTypeEnum.GRID : ViewTypeEnum.LIST;
    this.actionsService.onViewChange(next);
  }

  public onScroll(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.setCollapsed(window.scrollY > 18);
  }

  private setCollapsed(collapsed: boolean): void {
    if (this.collapsed === collapsed) {
      return;
    }
    this.collapsed = collapsed;
    this.collapsedChange.emit(collapsed);
  }

  private onSearchOverlayStateChange(open: boolean): void {
    this.searchOpen = open;
    if (!open) {
      this.removeSearchBackHandler?.();
      this.removeSearchBackHandler = null;
      this.searchQuery = '';
      return;
    }

    this.queryService.loading = true;
    this.removeSearchBackHandler?.();
    this.removeSearchBackHandler = this.mobileBack.register('mobile-top-navigation-search', () => {
      this.closeSearch();
      return true;
    });
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    setTimeout(() => this.mobileSearchInput?.nativeElement.focus());
  }

  private connectNotifications(): void {
    if (!this.user?.id || this.notificationSourceForUserId === this.user.id) {
      return;
    }
    this.notificationSourceForUserId = this.user.id;
    this.subscriptions.add(this.notifications.setEventSource(this.user).subscribe());
  }

  private updateDetailState(): void {
    const path = this.router.url.split('?')[0];
    this.isDetailView = !this.isRootView(path);
  }

  private isRootView(path: string): boolean {
    const normalized = path.replace(/\/+$/, '') || '/';
    if (['/', '/feed', '/marketplace', '/marketplace/offers', '/places', '/ranking', '/events', '/vehicles'].includes(normalized)) {
      return true;
    }

    const userSlug = this.user?.slug;
    return !!userSlug && new RegExp('^/' + userSlug + '/(?:garage|profile)(?:/(?:summary|follows|likes|events))?$').test(normalized);
  }

  private attachEdgeSwipe(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.removePointerDownListener = this.document.addEventListener
      ? this.listenPointer('pointerdown', (event) => this.onPointerDown(event))
      : null;
    this.removePointerUpListener = this.document.addEventListener
      ? this.listenPointer('pointerup', (event) => this.onPointerUp(event))
      : null;
  }

  private attachScrollListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const listener = () => this.onScroll();
    window.addEventListener('scroll', listener, {passive: true});
    this.removeScrollListener = () => window.removeEventListener('scroll', listener);
    // Emitting a parent layout state while this child is being created causes
    // Angular's development-mode expression check to see a changed value in
    // the same pass. Defer the initial persisted-scroll measurement by one
    // frame; subsequent scroll events remain immediate.
    this.initialScrollFrameId = requestAnimationFrame(() => {
      this.initialScrollFrameId = null;
      // On Safari WebView a restored scroll position can be applied in the
      // same frame as the component's first check. Emit to the parent only in
      // the next task, after Angular has completed that initial check.
      this.initialScrollTimeoutId = window.setTimeout(() => {
        this.initialScrollTimeoutId = null;
        this.onScroll();
      });
    });
  }

  private listenPointer(type: 'pointerdown' | 'pointerup', listener: (event: PointerEvent) => void): () => void {
    this.document.addEventListener(type, listener, {passive: true});
    return () => this.document.removeEventListener(type, listener);
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.hubOpen || this.isDetailView || this.hasBlockingOverlay() || event.clientX > 20 || this.isHorizontalGestureTarget(event.target)) {
      this.pointerStart = null;
      return;
    }
    this.pointerStart = {x: event.clientX, y: event.clientY, target: event.target};
  }

  private onPointerUp(event: PointerEvent): void {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start || this.hubOpen || this.isDetailView || this.hasBlockingOverlay() || this.isHorizontalGestureTarget(start.target)) {
      return;
    }
    const horizontalDistance = event.clientX - start.x;
    const verticalDistance = Math.abs(event.clientY - start.y);
    if (horizontalDistance >= 72 && horizontalDistance > verticalDistance * 1.5) {
      this.drawerRequested.emit();
    }
  }

  private isHorizontalGestureTarget(target: EventTarget | null): boolean {
    const element = target instanceof Element ? target : null;
    return !!element?.closest('.swiper, .p-galleria, [data-horizontal-gesture], [data-gallery], video');
  }

  private hasBlockingOverlay(): boolean {
    return !!this.document.querySelector('.p-dialog-mask, .p-sidebar-mask, .p-galleria-mask, app-overlay-gallery');
  }

  private async presentHub(): Promise<void> {
    await this.hubBottomSheet?.toggleBottomSheet();
    if (!this.hubOpen) {
      await this.hubBottomSheet?.closeIfPresented(false);
    }
  }
}
